import { Avatar, CircularProgress, FormControl, InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Booth, TeamMember } from '../../../../interfaces/interfaces';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../services/helper';
import { getAllUsers, updateBooth } from '../../../../services/services';
import VySelect from '../../../common/VySelect';

interface BoothLinkedPeopleSelectProps {
  booth: Booth;
  value?: TeamMember;
  isInline?: boolean;
  onChange?: (e: SelectChangeEvent<string>) => void;
  onSuccess?: () => void;
  onFinish?: () => void;
}

const BoothLinkedPeopleSelect = ({ booth, value, isInline, onChange, onSuccess, onFinish }: BoothLinkedPeopleSelectProps) => {
  const [loaded, setLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [isSelectingLinkedPerson, setIsSelectingLinkedPerson] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember>();
  const [mrError, setMrError] = useState('');
  const [orgMembers, setOrgMembers] = useState<TeamMember[]>([]);

  const user = useSelector((state) => state.common.user);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!user) return;
    if (loaded) return;
    if (!isInline) return;
    fetchMembers();
  }, [orgMembers, user]);

  useEffect(() => {
    if (!selectedMember) return;
    updateBoothLinkedPeople();
  }, [selectedMember]);

  const fetchMembers = async () => {
    if (!booth?.organization) return;
    setIsFetching(true);
    try {
      const { data } = await getAllUsers(50, undefined, undefined, booth?._id);
      setOrgMembers(data);
    } catch (error) {
      console.error(error);
    }
    setIsFetching(false);
    setLoaded(true);
  };

  async function updateBoothLinkedPeople() {
    setIsSelectingLinkedPerson(true);
    try {
      const linkedPeople = booth.linkedPeople ? [...booth.linkedPeople, { user: selectedMember }] : [{ user: selectedMember }];
      await updateBooth({ boothid: booth._id, linkedPeople });
      setSelectedMember(undefined);
      onSuccess?.();
      toast.success(t('toasts:success-new-linked-person'));
    } catch (err) {
      setMrError((err as any)?.response?.data?.translation?.[`${i18n.language}-${i18n.language.toUpperCase()}`] || t('common:error-without-status'));
    }
    setIsSelectingLinkedPerson(false);
    (document.activeElement as HTMLElement).blur();
  }

  return (
    <>
      {!isSelectingLinkedPerson && (
        <>
          {isFetching && <CircularProgress />}
          {!isFetching && (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="linked-people-select-outlined-label">{t('booth:linked-people-select-placeholder')}</InputLabel>

              <VySelect
                placeholder={t('booth:linked-people-select-placeholder')}
                labelId="linked-people-select-outlined-label"
                label={t('booth:linked-people-select-placeholder')}
                value={value?._id}
                onChange={(e) => setSelectedMember(orgMembers.find((user) => user._id === e.target.value))}>
                {orgMembers
                  .filter((user) => !booth.linkedPeople?.find((person) => person.user._id === user._id))
                  .filter((user) => user.name)
                  .map((user) => (
                    <MenuItem value={user._id}>
                      <div className="flex flex-row items-center w-full space-x-4">
                        <Avatar src={createDisplayLink(user.avatar, false)}>{user.name?.substr(0, 1)}</Avatar>
                        <p>{user.name}</p>
                      </div>
                    </MenuItem>
                  ))}
              </VySelect>
            </FormControl>
          )}
        </>
      )}
      {isSelectingLinkedPerson && <CircularProgress />}
    </>
  );
};

export default BoothLinkedPeopleSelect;
